import { ReactNode } from 'react';
import { t } from 'i18next';

import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import {
  Connectors,
  ConnectorsType,
  DocumentType,
  MessageState,
  MessageV2DTO,
  PartnerDTO,
  ReceiverEnvelopeState,
  TransactionDTO,
  TransactionFlowState,
  TransactionOriginApp,
  TransactionState,
  ValueOf,
} from 'support/types';
import { BadgeColor } from 'components/Display/Badge/Badge';
import { Translate } from 'components/Translate/Translate';

import { isMessageV2DTO } from '../../support/helpers/transactions/transactions';

export type StateContext = NonNullable<TransactionDTO['stateContext']>;

export const getColourAndText = (
  state: ValueOf<TransactionState> | ReceiverEnvelopeState.manuallyDelivered | ReceiverEnvelopeState.deliverySimulated,
  stateContext?: StateContext | null,
  connector?: ConnectorsType | null,
  isCurrentPartnerReceiver = false,
  internalState: MessageState | ReceiverEnvelopeState | null = null,
  deliveredWithoutEmail = false,
): { colour: BadgeColor; text: string; tooltip: string | ReactNode } => {
  switch (state) {
    case TransactionState.dropped:
      return {
        colour: BadgeColor.gray,
        text: t('transactions:show.sections.states.simple.dropped'),
        tooltip: t('transactions:show.sections.states.simple.droppedTooltip'),
      };
    case TransactionState.superseded:
      return {
        colour: BadgeColor.gray,
        text: t('transactions:show.sections.states.simple.superseded'),
        tooltip: t('transactions:show.sections.states.simple.supersededTooltip'),
      };
    case TransactionState.failed:
      return {
        colour: BadgeColor.red,
        text: t('transactions:show.sections.states.simple.failed'),
        tooltip: t('transactions:show.sections.states.simple.failedTooltip'),
      };
    case TransactionState.success:
      return {
        colour: BadgeColor.green,
        text: t('transactions:show.sections.states.simple.delivered'),
        tooltip: getDeliveredTooltip(deliveredWithoutEmail, isCurrentPartnerReceiver),
      };
    case ReceiverEnvelopeState.manuallyDelivered:
      return {
        colour: BadgeColor.green,
        text: t('transactions:show.sections.states.simple.manuallyDelivered'),
        tooltip: t('transactions:show.sections.states.simple.manuallyDeliveredTooltip'),
      };
    case ReceiverEnvelopeState.deliverySimulated:
      // Only the receiver reaches this state (gated in deriveState), so the tooltip is receiver-facing.
      return {
        colour: BadgeColor.blue,
        text: t('transactions:show.sections.states.simple.readyForDelivery'),
        tooltip: (
          <Translate
            i18nKey="transactions:show.sections.states.simple.readyForDeliveryTooltip"
            values={{ button: t('transactions:show.actionButtons.deliverTestMessage') }}
          />
        ),
      };
    case TransactionState.pending: {
      return {
        colour: BadgeColor.yellow,
        text: t('transactions:show.sections.states.simple.inProgress'),
        tooltip: getPendingTooltip(stateContext, connector, isCurrentPartnerReceiver, internalState),
      };
    }

    case TransactionState.pending_review:
      return {
        colour: BadgeColor.blue,
        text: t('transactions:show.sections.states.simple.pendingReview'),
        tooltip: t('transactions:show.sections.states.simple.pendingReviewTooltip'),
      };

    case TransactionState.missing:
      return {
        colour: BadgeColor.yellow,
        text: t('transactions:show.sections.states.simple.missing'),
        tooltip: t('transactions:show.sections.states.simple.missingTooltip'),
      };

    case TransactionState.unknown:
    default:
      return {
        colour: BadgeColor.yellow,
        text: t('transactions:show.sections.states.simple.unknown'),
        tooltip: '',
      };
  }
};

export const getReceiverPartnerId = (transaction: TransactionDTO | MessageV2DTO): string | undefined =>
  isMessageV2DTO(transaction) ? transaction.receiver_partner?.id : transaction.receiver?.id;

export const deriveState = (
  state: ValueOf<TransactionState>,
  transaction: TransactionDTO | MessageV2DTO,
  currentPartner: PartnerDTO,
) => {
  const receiverPartnerId = getReceiverPartnerId(transaction);
  // The transactions list exposes the receiver-envelope state via the top-level receiverEnvelopeState
  // field; fall back to internalState so the manuallyDelivered badge keeps working when it is absent.
  const receiverEnvelopeState = isMessageV2DTO(transaction)
    ? transaction.receiver_envelope?.state
    : (transaction.receiverEnvelopeState ?? transaction.internalState);

  if (currentPartner.id !== receiverPartnerId) {
    return state;
  }

  if (
    receiverEnvelopeState === ReceiverEnvelopeState.deliverySimulated &&
    transaction.flow === TransactionFlowState.test
  ) {
    return ReceiverEnvelopeState.deliverySimulated;
  }

  if (state !== TransactionState.success) {
    return state;
  }

  return receiverEnvelopeState === ReceiverEnvelopeState.manuallyDelivered
    ? ReceiverEnvelopeState.manuallyDelivered
    : state;
};

export const extractStateContext = (
  transaction: TransactionDTO | MessageV2DTO | null | undefined,
): StateContext | null => {
  if (!transaction) {
    return null;
  }
  return (isMessageV2DTO(transaction) ? transaction.state_context : transaction.stateContext) ?? null;
};

export const extractConnector = (
  transaction: TransactionDTO | MessageV2DTO | null | undefined,
): ConnectorsType | null => {
  if (!transaction) {
    return null;
  }
  const connector = isMessageV2DTO(transaction)
    ? transaction.receiver_integration?.connector
    : transaction.receiverEnvelope?.receiverIntegration?.connector;
  return connector ?? null;
};

export const extractInternalState = (
  transaction: TransactionDTO | MessageV2DTO | null | undefined,
): MessageState | ReceiverEnvelopeState | null => {
  if (!transaction) {
    return null;
  }
  if (isMessageV2DTO(transaction)) {
    if (transaction.receiver_envelope?.state === ReceiverEnvelopeState.pendingPickup) {
      return ReceiverEnvelopeState.pendingPickup;
    }
    if (transaction.internal_state === MessageState.pendingCollection) {
      return MessageState.pendingCollection;
    }
    return null;
  }
  if (transaction.internalState === ReceiverEnvelopeState.pendingPickup) {
    return ReceiverEnvelopeState.pendingPickup;
  }
  if (transaction.internalState === MessageState.pendingCollection) {
    return MessageState.pendingCollection;
  }
  return null;
};

/**
 * List rows carry the origin app themselves; a message detail payload does not, so its page passes
 * it in. The payload wins where it has one, so the passed-in value stays a stand-in for payloads
 * that cannot carry it rather than an override of one that can.
 */
export const extractOriginApp = (
  transaction: TransactionDTO | MessageV2DTO | null | undefined,
): TransactionOriginApp => {
  if (!transaction || isMessageV2DTO(transaction)) {
    return null;
  }
  return transaction.originApp ?? null;
};

/**
 * Narrow on purpose: the backend skips the notification for exactly one case, an order the Order
 * Agent read out of a PDF the receiver forwarded in, so widening either condition would claim
 * silence for documents that were in fact emailed. Absent email evidence alone does not prove
 * silence either, which is why both conditions have to hold.
 */
export const wasDeliveredWithoutEmail = (
  transaction: TransactionDTO | MessageV2DTO | null | undefined,
  originApp?: TransactionOriginApp,
): boolean => {
  if (!transaction || extractConnector(transaction) !== Connectors.WEB_EDI) {
    return false;
  }

  if ((extractOriginApp(transaction) ?? originApp) !== 'DOCUMENT_UNDERSTANDING_AGENT') {
    return false;
  }

  const documentType = isMessageV2DTO(transaction) ? transaction.type : transaction.documentType;
  if (documentType !== DocumentType.order) {
    return false;
  }

  return !hasEmailEvidence(transaction);
};

/**
 * Any record of a notification counts, not just one carrying an address: a recipient whose address
 * we never stored, or a proof of transmission whose notification log is gone, is still a document
 * that was emailed, and claiming silence for it would contradict the delivery record on the page.
 */
const hasEmailEvidence = (transaction: TransactionDTO | MessageV2DTO): boolean => {
  if ((extractStateContext(transaction)?.email?.recipients ?? []).length > 0) {
    return true;
  }

  const receiverEnvelope = isMessageV2DTO(transaction) ? transaction.receiver_envelope : transaction.receiverEnvelope;
  return (receiverEnvelope?.proofOfDelivery ?? []).length > 0;
};

const joinRecipientEmails = (stateContext?: StateContext | null): string =>
  (stateContext?.email?.recipients ?? [])
    .map((recipient) => recipient.destinationContact)
    .filter((email): email is string => Boolean(email))
    .join(', ');

const getDeliveredTooltip = (deliveredWithoutEmail: boolean, isCurrentPartnerReceiver: boolean): string => {
  if (!deliveredWithoutEmail) {
    return t('transactions:show.sections.states.simple.deliveredTooltip');
  }

  const perspective = isCurrentPartnerReceiver ? 'self' : 'other';
  return t(`transactions:show.sections.states.simple.deliveredWithoutEmailTooltip.${perspective}`);
};

/**
 * i18n keys
 * t('transactions:show.sections.states.simple.deliveredWithoutEmailTooltip.self')
 * t('transactions:show.sections.states.simple.deliveredWithoutEmailTooltip.other')
 */

const getPendingTooltip = (
  stateContext: StateContext | null | undefined,
  connector: ConnectorsType | null | undefined,
  isCurrentPartnerReceiver: boolean,
  internalState: MessageState | ReceiverEnvelopeState | null,
): string => {
  if (internalState === MessageState.pendingCollection) {
    const nextAttemptAt = stateContext?.schedule?.nextAttemptAt;
    return nextAttemptAt
      ? t('transactions:show.sections.states.simple.waitingToBeSentScheduledTooltip', {
          time: formatDayAndTime(nextAttemptAt),
        })
      : t('transactions:show.sections.states.simple.waitingToBeSentTooltip');
  }
  if (internalState !== ReceiverEnvelopeState.pendingPickup) {
    return t('transactions:show.sections.states.simple.inProgressTooltip');
  }

  const perspective = isCurrentPartnerReceiver ? 'self' : 'other';
  const recipientEmails = joinRecipientEmails(stateContext);
  if (connector === Connectors.WEB_EDI) {
    return recipientEmails
      ? t(`transactions:show.sections.states.simple.waitingForReceiverEmailWithAddressTooltip.${perspective}`, {
          email: recipientEmails,
        })
      : t(`transactions:show.sections.states.simple.waitingForReceiverEmailTooltip.${perspective}`);
  }
  return t(`transactions:show.sections.states.simple.waitingForReceiverPickupTooltip.${perspective}`);
};

/**
 * i18n keys
 * t('transactions:show.sections.states.simple.waitingForReceiverEmailTooltip.self')
 * t('transactions:show.sections.states.simple.waitingForReceiverEmailTooltip.other')
 * t('transactions:show.sections.states.simple.waitingForReceiverEmailWithAddressTooltip.self', { email })
 * t('transactions:show.sections.states.simple.waitingForReceiverEmailWithAddressTooltip.other', { email })
 * t('transactions:show.sections.states.simple.waitingForReceiverPickupTooltip.self')
 * t('transactions:show.sections.states.simple.waitingForReceiverPickupTooltip.other')
 */
